import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '@/seo/Seo';
import { Calendar, Clock, MapPin, CheckCircle2, ArrowLeft, BookOpen, HandHeart, Sun, Camera } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/client';
import LiveStreamingNotice from '@/components/LiveStreamingNotice';

const GENDERS = ['Male', 'Female'];
const RELATIONS = ['Mother', 'Father', 'Sister', 'Brother', 'Other'];
const TSHIRT_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL', 'Size 0 - 2', 'Size 2 - 4', 'Size 4 - 6', 'Size 6 - 8', 'Size 8 - 10', 'Size 10 - 12'];

const MEDIA_CONSENTS = [
  { key: 'consent_photos_activities', label: 'Permission for photographs during church activities' },
  { key: 'consent_photos_website',    label: 'Permission for photographs to appear on the Ministry of Jesus website' },
  { key: 'consent_photos_social',     label: 'Permission for photographs to appear on official church social-media pages' },
  { key: 'consent_video_online',      label: 'Permission for recorded video containing the child to appear online' },
  { key: 'consent_livestream',        label: 'Permission for participation in activities that may be live-streamed' },
];

const EMPTY = {
  email: '',
  student_name: '',
  gender: '',
  age: '',
  class_year: '',
  allergies: '',
  guardian_name: '',
  relationship: '',
  relationship_other: '',
  phone: '',
  tshirt_size: '',
  alternate_contact_name: '',
  alternate_contact_phone: '',
  // Granular media consent (each independently optional — never pre-selected)
  consent_photos_activities: false,
  consent_photos_website: false,
  consent_photos_social: false,
  consent_video_online: false,
  consent_livestream: false,
  // Parent / guardian block (mandatory for GDPR audit trail)
  parent_full_name: '',
  parent_email: '',
  parent_phone: '',
  consent_date: new Date().toISOString().slice(0, 10),
  consent_signature: '',
  consent_version: '2026-01',
  privacy_acknowledged: false,
  consent_given: false,
  send_copy: false,
};

function RadioGroup({ label, options, value, onChange, required, name }) {
  return (
    <fieldset>
      <legend className="label-moj">{label} {required && <span className="text-red-600">*</span>}</legend>
      <div className="flex flex-wrap gap-3 mt-1">
        {options.map((opt) => (
          <label key={opt} className={`flex items-center gap-2 px-4 py-2 border-2 rounded-full cursor-pointer transition ${value === opt ? 'border-moj-purple bg-moj-purple/5 text-moj-purple-deep font-semibold' : 'border-moj-gold/30 hover:border-moj-purple/50'}`}>
            <input type="radio" name={name} value={opt} checked={value === opt} onChange={() => onChange(opt)} className="sr-only"/>
            <span className={`w-4 h-4 rounded-full border-2 ${value === opt ? 'border-moj-purple bg-moj-purple' : 'border-moj-purple/40'}`}>
              {value === opt && <span className="block w-1.5 h-1.5 bg-white rounded-full m-auto mt-[3px]"/>}
            </span>
            <span className="text-sm">{opt}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function YesNoGroup({ label, value, onChange, required, name }) {
  return (
    <RadioGroup label={label} name={name} required={required}
      options={['Yes', 'No']}
      value={value === true ? 'Yes' : value === false ? 'No' : ''}
      onChange={(v) => onChange(v === 'Yes')}/>
  );
}

export default function VBSRegistration({ event }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    // Required-field client validation
    const required = ['email', 'student_name', 'gender', 'age', 'class_year', 'allergies', 'guardian_name', 'relationship', 'phone', 'tshirt_size', 'alternate_contact_name', 'alternate_contact_phone'];
    for (const k of required) {
      if (!form[k]) return toast.error(`Please complete: ${k.replace(/_/g, ' ')}`);
    }
    // Parent / guardian block for the GDPR consent audit trail
    if (!form.parent_full_name.trim()) return toast.error('Please provide Parent / Guardian full name');
    if (!form.parent_email.trim())     return toast.error('Please provide Parent / Guardian email');
    if (!form.parent_phone.trim())     return toast.error('Please provide Parent / Guardian phone');
    if (!form.consent_signature.trim()) return toast.error('Please provide a digital signature (Parent/Guardian typed name)');
    if (!form.privacy_acknowledged)    return toast.error('Please confirm you have read the privacy and media-consent information');
    if (!form.consent_given)           return toast.error('Please consent to submit your registration');
    setSubmitting(true);
    try {
      const payload = { ...form, age: parseInt(form.age, 10) };
      const { data } = await api.post(`/public/events/${event.slug}/register`, payload);
      setSuccess(data);
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit registration.');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <section className="py-20 bg-moj-cream">
        <div className="max-w-2xl mx-auto px-4 text-center card-moj p-10">
          <div className="w-20 h-20 rounded-full gradient-purple flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 size={40} className="text-moj-gold"/>
          </div>
          <h2 className="font-serif text-3xl text-moj-purple-deep mb-2">Thanksgiving received</h2>
          <p className="text-moj-charcoal mb-2 text-lg">{success.message}</p>
          <p className="text-moj-charcoal/70 text-sm mb-1">Registration ID: <code className="text-moj-purple">{success.registration_id}</code></p>
          <p className="text-moj-charcoal/70 text-sm mb-6">A confirmation will be sent to <strong>{form.email}</strong>. We can't wait to welcome {form.student_name}!</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate('/')} className="btn-primary">Back to Home</button>
            <button onClick={() => { setSuccess(null); setForm(EMPTY); window.scrollTo(0, 0); }} className="btn-outline">Register another child</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      <Seo
        title="VBS Registration — Vacation Bible School Dublin"
        description="Register your child for Ministry of Jesus Vacation Bible School in Dublin. Open to families across Dublin, Kildare, Meath and Wicklow."
        path={`/events/${event?.slug || 'vbs'}/rsvp`}
        keywords={['Vacation Bible School Dublin', 'VBS Ireland registration', 'children Bible camp Dublin']}
        noindex
      />
      <section className="gradient-purple text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/" className="text-moj-gold text-sm hover:underline"><ArrowLeft size={14} className="inline mr-1"/> Back to home</Link>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mt-4 mb-3 border border-moj-gold/30">
            <Sun size={12}/> Vacation Bible School 2026
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-3">Ministry of Jesus — VBS Registration Form</h1>
          <div className="flex flex-wrap gap-4 text-white/90 text-sm mt-3">
            <span className="flex items-center gap-1.5"><Calendar size={16} className="text-moj-gold"/> 3rd – 7th August 2026</span>
            <span className="flex items-center gap-1.5"><Clock size={16} className="text-moj-gold"/> 09:30 AM – 02:30 PM</span>
            <span className="flex items-center gap-1.5"><MapPin size={16} className="text-moj-gold"/> {event.location}</span>
          </div>
          <p className="text-white/80 text-sm mt-3">Family Day: 8th August, 5:00 PM – 8:00 PM. Please complete and submit by <strong className="text-moj-gold">31st July</strong>.</p>
        </div>
      </section>

      <section className="py-12 bg-moj-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <form onSubmit={onSubmit} className="space-y-6" data-testid="vbs-form">
            {/* Email */}
            <div className="card-moj p-6">
              <label className="label-moj" htmlFor="vbs-email">Email <span className="text-red-600">*</span></label>
              <input id="vbs-email" type="email" required className="input-moj" value={form.email}
                onChange={(e) => update('email', e.target.value)} placeholder="parent@example.com"/>
              <p className="text-xs text-moj-charcoal/60 mt-1">A confirmation will be sent here.</p>
            </div>

            {/* Student Section */}
            <div className="card-moj p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <BookOpen size={22} className="text-moj-gold-deep"/>
                <h2 className="font-serif text-xl text-moj-purple-deep">Student Information</h2>
              </div>

              <div>
                <label className="label-moj" htmlFor="vbs-student">Student's Name <span className="text-red-600">*</span></label>
                <input id="vbs-student" required className="input-moj" value={form.student_name} onChange={(e) => update('student_name', e.target.value)}/>
              </div>

              <RadioGroup label="Gender" name="gender" required options={GENDERS}
                value={form.gender} onChange={(v) => update('gender', v)}/>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-moj" htmlFor="vbs-age">Age <span className="text-red-600">*</span></label>
                  <input id="vbs-age" type="number" min={3} max={18} required className="input-moj" value={form.age} onChange={(e) => update('age', e.target.value)}/>
                </div>
                <div>
                  <label className="label-moj" htmlFor="vbs-class">Class / Year this September <span className="text-red-600">*</span></label>
                  <input id="vbs-class" required className="input-moj" value={form.class_year} onChange={(e) => update('class_year', e.target.value)} placeholder="e.g. 4th Class"/>
                </div>
              </div>

              <div>
                <label className="label-moj" htmlFor="vbs-allergies">Allergies (food or environmental) or other medical concerns we need to know <span className="text-red-600">*</span></label>
                <textarea id="vbs-allergies" required rows={3} className="input-moj" value={form.allergies}
                  onChange={(e) => update('allergies', e.target.value)} placeholder="Enter 'None' if not applicable"/>
              </div>

              <div>
                <label className="label-moj" htmlFor="vbs-tshirt">T-Shirt Size <span className="text-red-600">*</span></label>
                <select id="vbs-tshirt" required className="input-moj" value={form.tshirt_size} onChange={(e) => update('tshirt_size', e.target.value)}>
                  <option value="">Choose…</option>
                  {TSHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Parent/Guardian Section */}
            <div className="card-moj p-6 space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <HandHeart size={22} className="text-moj-gold-deep"/>
                <h2 className="font-serif text-xl text-moj-purple-deep">Parent / Guardian</h2>
              </div>

              <div>
                <label className="label-moj" htmlFor="vbs-guardian">Parent/Guardian Name <span className="text-red-600">*</span></label>
                <input id="vbs-guardian" required className="input-moj" value={form.guardian_name} onChange={(e) => update('guardian_name', e.target.value)}/>
              </div>

              <RadioGroup label="Relationship" name="relationship" required options={RELATIONS}
                value={form.relationship} onChange={(v) => update('relationship', v)}/>

              {form.relationship === 'Other' && (
                <div>
                  <label className="label-moj" htmlFor="vbs-rel-other">Please specify <span className="text-red-600">*</span></label>
                  <input id="vbs-rel-other" required className="input-moj" value={form.relationship_other} onChange={(e) => update('relationship_other', e.target.value)}/>
                </div>
              )}

              <div>
                <label className="label-moj" htmlFor="vbs-phone">Phone <span className="text-red-600">*</span></label>
                <input id="vbs-phone" required className="input-moj" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+353 87 1234567"/>
              </div>
            </div>

            {/* Emergency contact */}
            <div className="card-moj p-6 space-y-5">
              <h2 className="font-serif text-xl text-moj-purple-deep">Alternate Emergency Contact</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-moj" htmlFor="vbs-alt-name">Alternate Contact Name <span className="text-red-600">*</span></label>
                  <input id="vbs-alt-name" required className="input-moj" value={form.alternate_contact_name} onChange={(e) => update('alternate_contact_name', e.target.value)}/>
                </div>
                <div>
                  <label className="label-moj" htmlFor="vbs-alt-phone">Alternate Contact Phone <span className="text-red-600">*</span></label>
                  <input id="vbs-alt-phone" required className="input-moj" value={form.alternate_contact_phone} onChange={(e) => update('alternate_contact_phone', e.target.value)}/>
                </div>
              </div>
            </div>

            {/* Photography & Media Consent — replaces the old blanket photo/video consent */}
            <div className="card-moj p-6 space-y-5" data-testid="vbs-media-consent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-purple flex items-center justify-center flex-shrink-0">
                  <Camera size={18} className="text-moj-gold" />
                </div>
                <h2 className="font-serif text-xl text-moj-purple-deep m-0">Photography &amp; Media Consent</h2>
              </div>

              <LiveStreamingNotice />

              <p className="text-sm text-moj-charcoal">
                Media consent remains <strong>optional</strong> and does not affect
                participation in church programmes. Ministry of Jesus will take reasonable
                steps to respect recorded consent preferences.
              </p>

              <fieldset className="space-y-2" aria-labelledby="vbs-media-consent-legend">
                <legend id="vbs-media-consent-legend" className="sr-only">
                  Photography and media consent options
                </legend>
                {MEDIA_CONSENTS.map(({ key, label }) => {
                  const checked = !!form[key];
                  return (
                    <label
                      key={key}
                      className={`flex items-start gap-3 px-3 py-2 border-2 rounded-lg cursor-pointer transition ${
                        checked
                          ? 'border-moj-purple bg-moj-purple/5 text-moj-purple-deep'
                          : 'border-moj-gold/30 hover:border-moj-purple/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => update(key, e.target.checked)}
                        className="mt-1"
                        data-testid={`vbs-${key.replace(/_/g, '-')}`}
                      />
                      <span className="text-sm leading-relaxed">{label}</span>
                    </label>
                  );
                })}
              </fieldset>
            </div>

            {/* Parent / Guardian block — mandatory identification & audit trail */}
            <div className="card-moj p-6 space-y-5">
              <h2 className="font-serif text-xl text-moj-purple-deep">Parent / Guardian Confirmation</h2>
              <p className="text-sm text-moj-charcoal/80">
                Please provide parent or legal-guardian details. These details form part of
                the record of media consent and are stored securely in the authorised
                church administration system.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-moj" htmlFor="vbs-parent-name">
                    Parent / Guardian Full Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="vbs-parent-name"
                    required
                    className="input-moj"
                    autoComplete="name"
                    value={form.parent_full_name}
                    onChange={(e) => update('parent_full_name', e.target.value)}
                    data-testid="vbs-parent-full-name"
                  />
                </div>
                <div>
                  <label className="label-moj" htmlFor="vbs-parent-email">
                    Parent / Guardian Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="vbs-parent-email"
                    type="email"
                    required
                    className="input-moj"
                    autoComplete="email"
                    value={form.parent_email}
                    onChange={(e) => update('parent_email', e.target.value)}
                    data-testid="vbs-parent-email"
                  />
                </div>
                <div>
                  <label className="label-moj" htmlFor="vbs-parent-phone">
                    Parent / Guardian Phone <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="vbs-parent-phone"
                    type="tel"
                    required
                    className="input-moj"
                    autoComplete="tel"
                    value={form.parent_phone}
                    onChange={(e) => update('parent_phone', e.target.value)}
                    data-testid="vbs-parent-phone"
                  />
                </div>
                <div>
                  <label className="label-moj" htmlFor="vbs-consent-date">
                    Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="vbs-consent-date"
                    type="date"
                    required
                    className="input-moj"
                    value={form.consent_date}
                    onChange={(e) => update('consent_date', e.target.value)}
                    data-testid="vbs-consent-date"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-moj" htmlFor="vbs-signature">
                    Digital Signature / Confirmation <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="vbs-signature"
                    required
                    className="input-moj"
                    placeholder="Type your full name to confirm"
                    value={form.consent_signature}
                    onChange={(e) => update('consent_signature', e.target.value)}
                    data-testid="vbs-consent-signature"
                  />
                  <p className="text-xs text-moj-charcoal/60 mt-1">
                    By typing your full name you are electronically signing this consent
                    form on behalf of your child.
                  </p>
                </div>
              </div>
            </div>

            {/* Acknowledgement + data-handling consent */}
            <div className="card-moj p-6 space-y-4">
              <label className="flex items-start gap-2 text-sm text-moj-charcoal" data-testid="vbs-privacy-ack-label">
                <input
                  type="checkbox"
                  required
                  checked={form.privacy_acknowledged}
                  onChange={(e) => update('privacy_acknowledged', e.target.checked)}
                  className="mt-1"
                  data-testid="vbs-privacy-acknowledged"
                />
                <span>
                  Privacy and media-consent information has been read and understood.{' '}
                  <Link to="/privacy#children" className="text-moj-purple underline" target="_blank" rel="noopener noreferrer">
                    Read the Privacy Notice
                  </Link>
                  .
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-moj-charcoal">
                <input
                  type="checkbox"
                  required
                  checked={form.consent_given}
                  onChange={(e) => update('consent_given', e.target.checked)}
                  className="mt-1"
                  data-testid="vbs-consent-given"
                />
                <span>
                  I consent to Ministry of Jesus storing this registration data to organise
                  VBS 2026. See our{' '}
                  <Link to="/privacy" className="text-moj-purple underline">Privacy Notice</Link>.
                </span>
              </label>

              <label className="flex items-center gap-2 text-sm text-moj-charcoal">
                <input
                  type="checkbox"
                  checked={form.send_copy}
                  onChange={(e) => update('send_copy', e.target.checked)}
                />
                Send me a copy of my responses.
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(EMPTY)} className="btn-outline">Clear form</button>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="vbs-submit">
                {submitting ? 'Submitting…' : 'Submit Registration'}
              </button>
            </div>
            <p className="text-xs text-center text-moj-charcoal/60">Never submit passwords through this form.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
